import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@apollo/client";
import { Menu, Transition } from "@headlessui/react";
import { mdiSortVariant } from "@mdi/js";
import Icon from "@mdi/react";
import clsx from "clsx";

import truckEmpty from "assets/images/truck-empty.png";
import Header from "components/Header";
import { routes } from "constants/routes";
import { TRUCK_FILTER_ITEMS } from "constants/trucks";
import { showErrorMessage } from "helpers/toastMessage";
import MainLayout from "layouts/Main";
import { AppDispatch } from "store";
import { handleLoading } from "store/global/loader";
import QueryPostedTruckItem from "../modules/QueryPostedItem";
import { QUERY_MY_TRUCK_LIST } from "./query";
import styles from "./style.module.scss";

export default function MyPostedTrucks() {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const [filterType, setFilterType] = useState("-time_posted");
  const [myTrucksList, setMyTrucksList] = useState<any[]>([]);

  const ContainerRef = useRef<HTMLUListElement>(null);
  const [isScroll, setIsScroll] = useState(false);
  const [hasNextPage, setHasNextPage] = useState(true);
  const [endCursor, setEndCursor] = useState();

  //graphQL
  const { data, loading, error, refetch } = useQuery(QUERY_MY_TRUCK_LIST, {
    notifyOnNetworkStatusChange: true,
    variables: {
      orderBy: filterType,
      first: 20
    },
    fetchPolicy: "cache-and-network"
  });

  const selectedData = useMemo(
    () => myTrucksList.filter((item) => !item.isDeleted),
    [myTrucksList]
  );

  function goDetails(id: string) {
    navigate(`${routes.TRUCKS_MY_POSTED}/${id}`);
  }

  function editTruckItem(id: string) {
    navigate(`${routes.TRUCKS_POST}/${id}`);
  }

  function goMatches(id: string) {
    const url = routes.TRUCKS_MATCHES_LIST.replace(":truckId", id);
    navigate(url);
  }

  const refresh = async () => {
    await refetch();
  };

  const onScroll = async () => {
    if (ContainerRef.current) {
      const { scrollTop, offsetHeight, scrollHeight } = ContainerRef.current;
      if (!!scrollTop && scrollTop + offsetHeight + 1 >= scrollHeight && hasNextPage) {
        setIsScroll(true);
        await refetch({
          orderBy: "-time_posted",
          first: 20,
          after: endCursor
        });
      }
    }
  };

  useEffect(() => {
    if (data) {
      if (isScroll) {
        setMyTrucksList((prev) => [...prev, ...data.myTrucks.data]);
      } else {
        setMyTrucksList(data.myTrucks.data);
      }
      setHasNextPage(data.myTrucks.pageInfo.hasNextPage);
      setEndCursor(data.myTrucks.pageInfo.endCursor);
    }
  }, [data]);

  useEffect(() => {
    refresh();
  }, []);

  useEffect(() => {
    dispatch(handleLoading(loading));

    if (!loading && error) {
      showErrorMessage(error.message);
    }
  }, [loading]);

  return (
    <MainLayout
      desktopTitle="My Trucks"
      customHeader={<Header label="My Trucks" goBackPath={routes.TRUCKS} isHideDesktop />}
      className="px-5">
      <div className={styles.postedTrucksBody}>
        <Menu as="div" className={styles.menuContainer}>
          <Menu.Button className="bg-transparent">
            <Icon path={mdiSortVariant} />
          </Menu.Button>

          <Transition
            as={React.Fragment}
            enter="transition ease-out duration-300"
            enterFrom="transform opacity-0 scale-95"
            enterTo="transform opacity-100 scale-100"
            leave="transition ease-in duration-75"
            leaveFrom="transform opacity-100 scale-100"
            leaveTo="transform opacity-0 scale-95">
            <Menu.Items className={styles.itemWrapper}>
              {TRUCK_FILTER_ITEMS.map((item) => (
                <Menu.Item key={item.id}>
                  <button
                    type="button"
                    onClick={() => {
                      setIsScroll(false);
                      setFilterType(item.value);
                    }}
                    className={clsx(
                      styles.item,
                      filterType === item.value ? "text-primary" : "text-text"
                    )}>
                    {item.label}
                  </button>
                </Menu.Item>
              ))}
            </Menu.Items>
          </Transition>
        </Menu>

        <ul className={styles.postedTruckList} ref={ContainerRef} onScroll={onScroll}>
          {selectedData.length ? (
            selectedData.map((item) => (
              <QueryPostedTruckItem
                key={item.id}
                data={item}
                goDetail={goDetails}
                goMatches={goMatches}
                editTruckItem={editTruckItem}
                refresh={refresh}
              />
            ))
          ) : (
            <div className={styles.truckEmptyList}>
              <img src={truckEmpty} alt="Empty my posted truck list" />
              <h3 className={styles.truckEmptyTitle}>You have no posted trucks</h3>
            </div>
          )}
        </ul>
      </div>
    </MainLayout>
  );
}
